import React from 'react'
import { cn } from '@/utils/cn'

interface TutaraProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number
  max?: number
  variant?: 'default' | 'gradient' | 'striped'
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  label?: string
}

const TutaraProgress = React.forwardRef<HTMLDivElement, TutaraProgressProps>(
  (
    {
      className,
      value = 0,
      max = 100,
      variant = 'default',
      size = 'md',
      showLabel = false,
      label,
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(100, Math.max(0, (value / max) * 100))

    const sizeClasses = {
      sm: 'h-2',
      md: 'h-3',
      lg: 'h-4',
    }

    const variantClasses = {
      default: 'bg-tutara-turquoise',
      gradient: 'bg-gradient-to-r from-tutara-teal to-tutara-turquoise',
      striped: 'bg-tutara-turquoise bg-stripes',
    }

    return (
      <div className={cn('w-full', className)} {...props}>
        {(showLabel || label) && (
          <div className="flex justify-between items-center mb-1">
            {label && (
              <span className="text-sm font-medium text-tutara-charcoal dark:text-gray-200">
                {label}
              </span>
            )}
            {showLabel && (
              <span className="text-sm font-semibold text-tutara-teal dark:text-teal-300">
                {percentage.toFixed(0)}%
              </span>
            )}
          </div>
        )}
        <div
          ref={ref}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
          className={cn(
            'w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700',
            sizeClasses[size]
          )}
        >
          <div
            className={cn(
              'h-full transition-all duration-300 ease-out rounded-full',
              variantClasses[variant],
              variant === 'striped' && 'animate-stripes'
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    )
  }
)

TutaraProgress.displayName = 'TutaraProgress'

// Circular Progress variant
interface TutaraCircularProgressProps {
  value?: number
  max?: number
  size?: number
  strokeWidth?: number
  showLabel?: boolean
  className?: string
}

export const TutaraCircularProgress: React.FC<TutaraCircularProgressProps> = ({
  value = 0,
  max = 100,
  size = 120,
  strokeWidth = 8,
  showLabel = true,
  className,
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100))
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (percentage / 100) * circumference

  return (
    <div className={cn('relative inline-flex', className)}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-tutara-turquoise transition-all duration-300 ease-out"
          strokeLinecap="round"
        />
      </svg>
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-tutara-teal dark:text-teal-300">
            {percentage.toFixed(0)}%
          </span>
        </div>
      )}
    </div>
  )
}

export { TutaraProgress }